import { useState, useEffect, useRef } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import "./AdvancedSearch.scss";

const AdvancedSearch = ({
  onSearch,
  categoriasStock,
  armazens = [],
  localizacoes = [],
  initialFilters = {
    nome: "",
    categoriaStock: "todas",
    estoqueStatus: "todos",
    validadeStatus: "todos",
    armazem: "todos",
    localizacao: "todas",
    incluirEsgotados: false,
  },
}) => {
  const [filters, setFilters] = useState(initialFilters);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const localizacoesFiltradas =
    filters.armazem === "todos"
      ? localizacoes
      : localizacoes.filter(
          (l) =>
            l.armazem?.id === filters.armazem || l.armazem === filters.armazem,
        );

  // Lista de opções para os filtros dropdown
  const estoqueOptions = [
    { value: "todos", label: "Todos os produtos" },
    { value: "baixo", label: "Estoque baixo" },
    { value: "esgotado", label: "Esgotado" },
    { value: "adequado", label: "Estoque adequado" },
  ];

  const validadeOptions = [
    { value: "todos", label: "Todas as validades" },
    { value: "vencido", label: "Produtos vencidos" },
    { value: "30dias", label: "Vencendo em 30 dias" },
    { value: "90dias", label: "Vencendo em 90 dias" },
  ];

  // Executa a busca com os filtros definidos
  const prevNomeRef = useRef(filters.nome);

  useEffect(() => {
    const nomeChanged = prevNomeRef.current !== filters.nome;
    prevNomeRef.current = filters.nome;

    const delay = nomeChanged ? 600 : 0;

    const timeout = setTimeout(() => {
      onSearch(filters);
    }, delay);

    return () => clearTimeout(timeout);
  }, [filters]);

  // Atualiza os filtros quando o usuário digita
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "armazem" ? { localizacao: "todas" } : {}),
    }));
  };

  // Limpa todos os filtros
  const handleClearFilters = () => {
    setFilters(initialFilters);
    onSearch(initialFilters);
  };

  // Função recursiva para encontrar o nome da categoria por ID
  const findCategoryNameById = (categories, id) => {
    if (!categories) return id;

    // Converter id para string para comparações consistentes
    const idString = String(id);

    // Busca na lista atual
    const foundCategory = categories.find((cat) => String(cat.id) === idString);
    if (foundCategory) return foundCategory.nome;

    // Busca nas subcategorias
    for (const category of categories) {
      if (category.subcategorias && category.subcategorias.length > 0) {
        const found = findCategoryNameById(category.subcategorias, id);
        if (found !== idString && found !== id) return found;
      }
    }

    return id; // Se não encontrar, retorna o ID como fallback
  };

  // Renderiza opções de categorias com indentação para subcategorias
  const renderCategoryOptions = (categories, level = 0) => {
    if (!categories) return null;

    return categories.flatMap((category) => [
      <option
        key={category.id}
        value={category.id}
        style={{
          paddingLeft: `${level * 20}px`,
          fontWeight: level === 0 ? "bold" : "normal",
        }}
      >
        {level > 0
          ? `${"\u00A0".repeat(level * 2)}└─ ${category.nome}`
          : category.nome}
      </option>,
      ...(category.subcategorias
        ? renderCategoryOptions(category.subcategorias, level + 1)
        : []),
    ]);
  };

  return (
    <div className="advanced-search">
      <form>
        {/* Barra de busca principal */}
        <div className="advanced-search__main">
          <div className="advanced-search__input-container">
            <Search className="advanced-search__icon" />
            <input
              type="text"
              name="nome"
              value={filters.nome}
              onChange={handleInputChange}
              placeholder="Pesquisar produto por nome..."
              className="advanced-search__input"
            />
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="advanced-search__filters-toggle"
          >
            <SlidersHorizontal className="advanced-search__filters-icon" />
            {showAdvanced ? "Menos filtros" : "Mais filtros"}
          </button>
        </div>

        {/* Opções de filtro avançado */}
        {showAdvanced && (
          <div className="advanced-search__filters">
            <div className="advanced-search__filter">
              <label className="advanced-search__label">Categoria</label>
              <select
                name="categoriaStock"
                value={filters.categoriaStock}
                onChange={handleInputChange}
                className="advanced-search__select"
              >
                <option value="todas">Todas as categorias</option>
                {renderCategoryOptions(categoriasStock)}
              </select>
            </div>

            <div className="advanced-search__filter">
              <label className="advanced-search__label">
                Status de Estoque
              </label>
              <select
                name="estoqueStatus"
                value={filters.estoqueStatus}
                onChange={handleInputChange}
                className="advanced-search__select"
              >
                {estoqueOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="advanced-search__filter">
              <label className="advanced-search__label">
                Status de Validade
              </label>
              <select
                name="validadeStatus"
                value={filters.validadeStatus}
                onChange={handleInputChange}
                className="advanced-search__select"
              >
                {validadeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="advanced-search__filter">
              <label className="advanced-search__label">Armazém</label>
              <select
                name="armazem"
                value={filters.armazem}
                onChange={handleInputChange}
                className="advanced-search__select"
              >
                <option value="todos">Todos os armazéns</option>
                {armazens.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.codigo} — {a.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="advanced-search__filter">
              <label className="advanced-search__label">Localização</label>
              <select
                name="localizacao"
                value={filters.localizacao}
                onChange={handleInputChange}
                className="advanced-search__select"
                disabled={
                  filters.armazem === "todos" &&
                  localizacoesFiltradas.length === 0
                }
              >
                <option value="todas">Todas as localizações</option>
                {localizacoesFiltradas.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.codigo} — {l.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Mostrar tags dos filtros ativos */}
        {(filters.nome ||
          filters.categoriaStock !== "todas" ||
          filters.estoqueStatus !== "todos" ||
          filters.validadeStatus !== "todos" ||
          filters.armazem !== "todos" ||
          filters.localizacao !== "todas") && (
          <div className="advanced-search__active-filters">
            <div className="advanced-search__active-filters-label">
              Filtros ativos:
            </div>

            {filters.nome && (
              <div className="advanced-search__tag advanced-search__tag--blue">
                <span>Nome: {filters.nome}</span>
                <X
                  className="advanced-search__tag-icon"
                  onClick={() => setFilters((prev) => ({ ...prev, nome: "" }))}
                />
              </div>
            )}

            {filters.categoriaStock !== "todas" && (
              <div className="advanced-search__tag advanced-search__tag--green">
                <span>
                  Categoria:{" "}
                  {findCategoryNameById(
                    categoriasStock,
                    filters.categoriaStock,
                  )}
                </span>
                <X
                  className="advanced-search__tag-icon"
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, categoriaStock: "todas" }))
                  }
                />
              </div>
            )}

            {filters.estoqueStatus !== "todos" && (
              <div className="advanced-search__tag advanced-search__tag--yellow">
                <span>
                  Estoque:{" "}
                  {
                    estoqueOptions.find(
                      (o) => o.value === filters.estoqueStatus,
                    )?.label
                  }
                </span>
                <X
                  className="advanced-search__tag-icon"
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, estoqueStatus: "todos" }))
                  }
                />
              </div>
            )}

            {filters.validadeStatus !== "todos" && (
              <div className="advanced-search__tag advanced-search__tag--red">
                <span>
                  Validade:{" "}
                  {
                    validadeOptions.find(
                      (o) => o.value === filters.validadeStatus,
                    )?.label
                  }
                </span>
                <X
                  className="advanced-search__tag-icon"
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, validadeStatus: "todos" }))
                  }
                />
              </div>
            )}

            {filters.armazem !== "todos" && (
              <div className="advanced-search__tag advanced-search__tag--blue">
                <span>
                  Armazém:{" "}
                  {armazens.find((a) => a.id === filters.armazem)?.nome ||
                    filters.armazem}
                </span>
                <X
                  className="advanced-search__tag-icon"
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      armazem: "todos",
                      localizacao: "todas",
                    }))
                  }
                />
              </div>
            )}

            {filters.localizacao !== "todas" && (
              <div className="advanced-search__tag advanced-search__tag--green">
                <span>
                  Localização:{" "}
                  {localizacoes.find((l) => l.id === filters.localizacao)
                    ?.nome || filters.localizacao}
                </span>
                <X
                  className="advanced-search__tag-icon"
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, localizacao: "todas" }))
                  }
                />
              </div>
            )}

            <button
              type="button"
              onClick={handleClearFilters}
              className="advanced-search__clear-button"
            >
              Limpar todos
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default AdvancedSearch;
